// PanelHome — the Kontext panel's home (right-panel coherence v2 + Kontext
// goal): groups, each with its own heading and tinted glyph, no borders.
//
//   0. Goal — what the chat is for: its goals, objectives and terms with
//      coverage, or one invitation to set a goal (the body comes in as
//      `goal`, GoalViews.tsx).
//   1. Material in this chat — what the chat is making; each row answers the
//      lens questions and opens the material's detail view. A course lists
//      its parts beneath it; a part opens the course at that part.
//   2. Sources and where they are used — each source says where it is cited
//      (claims per material, replies in the chat); attached-but-unused last.
//   3. Other files — hidden when empty.
//
// Every group heading folds its group (owner 2026-10-06: filter out what you
// are not working on); the fold is a per-device preference the host keeps
// (`kontextFold`), passed in. A pure projection of `PanelHome`
// (rightPanel/home.ts) plus intents out; no local state.

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { EmptyState } from "../../../components/EmptyState/EmptyState.js";
import {
    factCheckRead,
    verdictOf,
} from "../../../components/FactCheckPanel/FactCheckPanel.js";
import { Icon, type IconName } from "../../../components/Icon/Icon.js";
import type {
    MaterialRef,
    ResourceRef,
    SourceRef,
} from "../../../components/Navigation/types.js";
import { pedagogyRead } from "../../../components/PedagogicalQuality/PedagogicalQuality.js";
import { Text } from "../../../components/Text/Text.js";
import { formatCount } from "../../displayLimits.js";
import {
    isFolded,
    type KontextFold,
    type KontextGroup,
} from "../../kontextFold.js";
import {
    factCheckModel,
    pedagogicalQualityModel,
} from "../reviewQualityModel.js";
import {
    type ChatMaterial,
    type PanelHome as Home,
    isUnused,
    type OtherFile,
    replyIdsOf,
    type SourceUsage,
} from "./home.js";
import { type Tone, toneOfVerdict } from "./LensBar.js";
import "./RightPanel.css";
import "./Goal.css";

// A material's answer to the third lens, with its tone.
type GoalLine = { readonly answer: string; readonly tone: Tone };

export type PanelHomeProps = {
    readonly home: Home;
    // The "Mål" group's body (GoalGroupBody), null for no goal group at all,
    // and whether the chat has a goal.
    readonly goal: ReactNode | null;
    readonly hasGoal: boolean;
    // A material's answer to "Does it meet the goals?", when the chat has one.
    readonly goalLineOf: (materialId: string) => GoalLine | undefined;
    readonly fold: KontextFold;
    readonly onToggleGroup: (group: KontextGroup) => void;
    readonly onOpenMaterial: (ref: MaterialRef) => void;
    readonly onOpenSource: (ref: SourceRef) => void;
    // Jump to (and flash) the chat replies that cite a source.
    readonly onShowReplies: (entryIds: readonly string[]) => void;
    readonly onOpenOther: (ref: ResourceRef) => void;
};

const Dot = ({ tone }: { readonly tone: Tone }): ReactNode => (
    <span className={`bilda-rp-dot bilda-rp-dot--${tone}`} aria-hidden="true" />
);

// One home group: its heading folds it (a disclosure button inside the
// heading, so the heading keeps its level for screen readers).
const Group = ({
    group,
    icon,
    tone,
    title,
    count,
    fold,
    onToggle,
    children,
}: {
    readonly group: KontextGroup;
    readonly icon: IconName;
    readonly tone: "goal" | "material" | "source" | "other";
    readonly title: string;
    readonly count?: number;
    readonly fold: KontextFold;
    readonly onToggle: (group: KontextGroup) => void;
    readonly children: ReactNode;
}): ReactNode => {
    const { i18n } = useLingui();
    const open = !isFolded(fold, group);
    return (
        <section className="bilda-rp-group">
            <h3 className="bilda-rp-group__heading">
                <button
                    type="button"
                    className="bilda-rp-row bilda-rp-fold"
                    aria-expanded={open}
                    onClick={() => onToggle(group)}
                >
                    <span className="bilda-rp-group__title">
                        <span
                            className={`bilda-rp-glyph bilda-rp-glyph--${tone}`}
                            aria-hidden="true"
                        >
                            <Icon name={icon} size="sm" />
                        </span>
                        <Text as="span" size="sm" weight="semibold">
                            {title}
                        </Text>
                        {count === undefined ? null : (
                            <Text as="span" size="sm" tone="faint">
                                {formatCount(count, i18n.locale)}
                            </Text>
                        )}
                    </span>
                    <Icon
                        name={open ? "chevron-up" : "chevron-down"}
                        size="xs"
                    />
                </button>
            </h3>
            {open ? children : null}
        </section>
    );
};

const MaterialRow = ({
    material,
    goalLine,
    onOpen,
}: {
    readonly material: ChatMaterial;
    readonly goalLine: GoalLine | undefined;
    readonly onOpen: (ref: MaterialRef) => void;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const factModel = factCheckModel(material.quality);
    const fact = factCheckRead(factModel)
        .headlines.map((h) => i18n._(h))
        .join(", ");
    const ped = pedagogyRead(pedagogicalQualityModel(material.quality));
    const teaching = i18n._(ped.headline);
    return (
        <li>
            <button
                type="button"
                className="bilda-rp-material"
                onClick={() => onOpen(material.ref)}
            >
                <Text
                    as="span"
                    size="sm"
                    weight="medium"
                    className="bilda-rp-title"
                >
                    {material.ref.title}
                </Text>
                <span className="bilda-rp-verdict">
                    <Dot tone={toneOfVerdict(verdictOf(factModel))} />
                    <Text as="span" size="xs" tone="dim">
                        {t`Grounded: ${fact}`}
                    </Text>
                </span>
                <span className="bilda-rp-verdict">
                    <Dot tone={ped.tone} />
                    <Text as="span" size="xs" tone="dim">
                        {t`Teaches: ${teaching}`}
                    </Text>
                </span>
                {goalLine === undefined ? null : (
                    <span className="bilda-rp-verdict">
                        <Dot tone={goalLine.tone} />
                        <Text as="span" size="xs" tone="dim">
                            {t`Goals: ${goalLine.answer}`}
                        </Text>
                    </span>
                )}
            </button>
            {material.parts.length === 0 ? null : (
                <ul className="bilda-rp-uses" aria-label={t`Parts`}>
                    {material.parts.map((part) => (
                        <li key={part.id}>
                            <button
                                type="button"
                                className="bilda-rp-row bilda-rp-use"
                                onClick={() => onOpen(part)}
                            >
                                <Icon name="material" size="xs" />
                                <Text as="span" size="xs" tone="dim">
                                    {part.title}
                                </Text>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
};

const SourceRow = ({
    usage,
    onOpen,
    onShowReplies,
}: {
    readonly usage: SourceUsage;
    readonly onOpen: (ref: SourceRef) => void;
    readonly onShowReplies: (entryIds: readonly string[]) => void;
}): ReactNode => {
    const { t } = useLingui();
    const open = () => onOpen(usage.source.ref);
    const replies = replyIdsOf(usage);
    const via = usage.source.viaGoal;
    return (
        <li className="bilda-rp-source">
            <button
                type="button"
                className="bilda-rp-row bilda-rp-source__title"
                onClick={open}
            >
                <Text
                    as="span"
                    size="sm"
                    weight="medium"
                    className="bilda-rp-title"
                >
                    {usage.source.ref.title}
                </Text>
            </button>
            {via === undefined ? null : (
                <Text as="p" size="xs" tone="faint" className="bilda-rp-quiet">
                    {t`Via the goal ${via}`}
                </Text>
            )}
            {isUnused(usage) ? (
                <Text as="p" size="xs" tone="faint" className="bilda-rp-quiet">
                    {t`Not used yet`}
                </Text>
            ) : (
                <ul className="bilda-rp-uses">
                    {usage.inMaterials.map((use) => (
                        <li key={use.material.id}>
                            <button
                                type="button"
                                className="bilda-rp-row bilda-rp-use"
                                onClick={open}
                            >
                                <Icon name="anchor" size="xs" />
                                <Text as="span" size="xs" tone="dim">
                                    {t`${plural(use.rows.length, {
                                        one: "Cited in # place in",
                                        other: "Cited in # places in",
                                    })}`}{" "}
                                    <em>{use.material.title}</em>
                                </Text>
                            </button>
                        </li>
                    ))}
                    {replies.length === 0 ? null : (
                        <li>
                            <button
                                type="button"
                                className="bilda-rp-row bilda-rp-use"
                                onClick={() => onShowReplies(replies)}
                            >
                                <Icon name="chat" size="xs" />
                                <Text as="span" size="xs" tone="dim">
                                    {t`${plural(replies.length, {
                                        one: "Cited in # reply in the chat",
                                        other: "Cited in # replies in the chat",
                                    })}`}
                                </Text>
                            </button>
                        </li>
                    )}
                </ul>
            )}
        </li>
    );
};

const otherIcon: Record<OtherFile["type"], IconName> = {
    "material-elsewhere": "material",
    "read-uncited": "source",
    package: "export",
    file: "file",
};

const OtherFiles = ({
    others,
    onOpen,
}: {
    readonly others: readonly OtherFile[];
    readonly onOpen: (ref: ResourceRef) => void;
}): ReactNode => {
    const { t } = useLingui();
    const why: Record<OtherFile["type"], string> = {
        "material-elsewhere": t`Material from another chat, mentioned here`,
        "read-uncited": t`Read by the agent, nothing cites it`,
        package: t`Package made in this chat`,
        file: t`File added in this chat`,
    };
    return (
        <ul className="bilda-rp-list">
            {others.map((o) => (
                <li key={o.ref.id}>
                    <button
                        type="button"
                        className="bilda-rp-row bilda-rp-other"
                        onClick={() => onOpen(o.ref)}
                    >
                        <Icon name={otherIcon[o.type]} size="xs" />
                        <span className="bilda-rp-other__text">
                            <Text
                                as="span"
                                size="sm"
                                className="bilda-rp-title"
                            >
                                {o.ref.title}
                            </Text>
                            <Text as="span" size="xs" tone="faint">
                                {why[o.type]}
                            </Text>
                        </span>
                    </button>
                </li>
            ))}
        </ul>
    );
};

export const PanelHome = ({
    home,
    goal,
    hasGoal,
    goalLineOf,
    fold,
    onToggleGroup,
    onOpenMaterial,
    onOpenSource,
    onShowReplies,
    onOpenOther,
}: PanelHomeProps): ReactNode => {
    const { t } = useLingui();
    const goalGroup =
        goal === null ? null : (
            <Group
                group="goal"
                icon="star"
                tone="goal"
                title={t`Goal`}
                fold={fold}
                onToggle={onToggleGroup}
            >
                {goal}
            </Group>
        );
    // Nothing yet and no goal: the goal invitation, then one calm empty state.
    // With a goal, the empty state steps aside (the goal IS the content).
    if (home.type === "empty" && !hasGoal)
        return (
            <div className="bilda-rp-home">
                {goalGroup}
                <div className="bilda-rp-empty">
                    <EmptyState
                        icon={<Icon name="material" size="lg" />}
                        title={t`What this chat makes gathers here`}
                        body={t`Materials the chat creates show up here, with the sources they rest on. Attach a source or ask the agent to start a material.`}
                    />
                </div>
            </div>
        );
    const { materials, sources, others } =
        home.type === "empty"
            ? { materials: [], sources: [], others: [] }
            : home;
    return (
        <div className="bilda-rp-home">
            {goalGroup}
            <Group
                group="materials"
                icon="material"
                tone="material"
                title={t`Material in this chat`}
                fold={fold}
                onToggle={onToggleGroup}
            >
                {materials.length === 0 ? (
                    <Text
                        as="p"
                        size="sm"
                        tone="dim"
                        className="bilda-rp-placeholder"
                    >
                        {t`No material yet. Ask the agent to make one from the sources below.`}
                    </Text>
                ) : (
                    <ul className="bilda-rp-list">
                        {materials.map((m) => (
                            <MaterialRow
                                key={m.ref.id}
                                material={m}
                                goalLine={goalLineOf(m.ref.id)}
                                onOpen={onOpenMaterial}
                            />
                        ))}
                    </ul>
                )}
            </Group>
            {sources.length === 0 ? null : (
                <Group
                    group="sources"
                    icon="source"
                    tone="source"
                    title={t`Sources and where they are used`}
                    fold={fold}
                    onToggle={onToggleGroup}
                >
                    <ul className="bilda-rp-list">
                        {sources.map((u) => (
                            <SourceRow
                                key={u.source.ref.id}
                                usage={u}
                                onOpen={onOpenSource}
                                onShowReplies={onShowReplies}
                            />
                        ))}
                    </ul>
                </Group>
            )}
            {others.length === 0 ? null : (
                <Group
                    group="others"
                    icon="file"
                    tone="other"
                    title={t`Other files`}
                    count={others.length}
                    fold={fold}
                    onToggle={onToggleGroup}
                >
                    <OtherFiles others={others} onOpen={onOpenOther} />
                </Group>
            )}
        </div>
    );
};
